// Firebase auth dynamically imported to avoid blocking render
export async function fetchWithAuth(url: string, options: RequestInit = {}) {
  let user = null;
  try {
    const { auth } = await import('../firebase');
    user = auth.currentUser;
  } catch (e) {
    console.warn("Dynamic import of Firebase auth failed in api.ts:", e);
  }
  const initHeaders = (options.headers || {}) as Record<string, string>;
  const headers: Record<string, string> = { ...initHeaders };

  // Only set application/json if we are not sending FormData
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (user) {
    const token = await user.getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    // Fallback: check local storage for custom session (for users not signed into Firebase Auth)
    try {
      const saved = localStorage.getItem('paklegal_user_session');
      if (saved) {
        const userData = JSON.parse(saved);
        if (userData && userData.id) {
          // Generate a minimal fake JWT structure that the server's fallback can decode
          const payloadObj = { 
            uid: userData.id, 
            email: userData.email, 
            role: userData.role,
            name: userData.name
          };
          const payloadStr = btoa(JSON.stringify(payloadObj));
          const fakeToken = `dummy_header.${payloadStr}.dummy_signature`;
          headers['Authorization'] = `Bearer ${fakeToken}`;
        } else {
          // Absolute fallback for completely anonymous guests
          const guestPayload = { uid: 'guest_' + Date.now(), role: 'Guest', name: 'Guest User' };
          const payloadStr = btoa(JSON.stringify(guestPayload));
          headers['Authorization'] = `Bearer dummy_header.${payloadStr}.dummy_signature`;
        }
      } else {
        // Absolute fallback for completely anonymous guests
        const guestPayload = { uid: 'guest_' + Date.now(), role: 'Guest', name: 'Guest User' };
        const payloadStr = btoa(JSON.stringify(guestPayload));
        headers['Authorization'] = `Bearer dummy_header.${payloadStr}.dummy_signature`;
      }
    } catch (e) {
      console.warn("API Auth fallback failed:", e);
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}
